<template>
  <v-theme-provider :theme="appTheme">
    <cs-app
      class="stb-app"
      :show-header="showHeader"
      :app-logo-uri="stbHorizontalLogo"
      :header-profile="headerProfile"
      :feedback-btn="feedbackBtn"
      :help-btn="helpBtn"
      :settings-btn="settingsBtn"
      :side-nav-width="sideNavWidth"
      :version="version"
      home-route="/excursions"
      @feedback-btn-click="registerFeedbackLinkClickEvent($event, '/feedback')"
    >
      <template #header-left>
        <div class="topbar-actions">
          <v-icon
            size="28"
            color="white"
            class="user-info-icon mr-2 mt-2"
            @click="userInfoDisplayed = true"
            >mdi-account</v-icon
          >
          <cs-button
            v-if="textButtonDisplayed(excursionCreationTextButton)"
            :id="excursionCreationTextButton.name"
            :href="excursionCreationTextButton.to"
            :label="excursionCreationTextButton.label"
            primary
          ></cs-button>
        </div>
      </template>

      <template #side-nav>
        <app-side-nav></app-side-nav>
      </template>

      <template #router-view>
        <v-slide-y-transition>
          <div :class="contentHtmlClassConcat">
            <router-view v-slot="{ Component }">
              <component :is="Component" />
            </router-view>
          </div>
        </v-slide-y-transition>
      </template>

      <template #footer>
        <div :class="footerClass">
          <div v-if="isDemoEnvironment" class="demo-note">
            PLEASE NOTE: This is a demo site only, please do not enter any personal information.
            Information entered here may be deleted at any time.
          </div>
          <div class="d-flex flex-column align-center justify-center">
            <v-img :src="complispaceLogo" width="200px" height="55px" class="img" />
            <span>
              &copy;{{ getCopyrightYearRange() }} CompliSpace (v{{ version }}). All rights reserved.
            </span>
          </div>
        </div>
      </template>
    </cs-app>
    <cs-form-dialog
      id="dlg-response-with-error"
      v-model="isResponseWithError"
      body-id="dlg-body-response-with-error"
      heading="Server Error"
      :primary-action="{
        label: 'Close',
        eventName: 'primary-click'
      }"
      @primary-click="closeErrorMessageDialogBtnClick"
    >
      <template #cs-form-dialog-content>
        There seems to be an issue with the server, please try again later or log a support request.
        <div v-safe-html="debugReferenceMessage"></div>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-user-info"
      v-model="userInfoDisplayed"
      body-id="dlg-body-user-info"
      heading="User Information"
      :primary-action="{
        label: 'Close',
        eventName: 'primary-click'
      }"
      @primary-click="userInfoDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <div v-safe-html="userInfo"></div>
      </template>
    </cs-form-dialog>
  </v-theme-provider>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { CSHeaderProfileProps, CSHeaderButtonProps } from '@complispace/cs-design-system';
import capitalize from 'lodash/capitalize';
import sessionManager from '@/store/modules/session/sessionManager';
import * as types from './store/mutationTypes';
import defaultClientLogo from './assets/image/YourSchoolLogo.png';
import trackableEvent from './lib/const/trackableEvent';
import complispaceLogo from './assets/image/ComplispaceLogo.png';
import stbHorizontalLogo from './assets/image/StbHorizontalNavLogo.png';
import { populateErrorMessage } from './lib/errorMessages';
import topToolbarTextButtons from './lib/const/topToolbarTextButtons';
import AppSideNav from './components/AppSideNav';

import packageParam from '../package.json';

export default {
  name: 'App',

  components: {
    AppSideNav
  },

  data() {
    return {
      appTheme: 'light',
      vendorIntegrationWindowViewEnabled: false,
      fixed: true,
      excursionTypes: ['Type1', 'Type2'],
      riskCategories: [
        'High-Risk Student Medical Conditions',
        'Transport',
        'Venues',
        'Food and Beverage',
        'Activity'
      ],
      riskClasses: ['Swimming', 'Boarding'],
      excursionActive: false,
      complispaceLogo,
      stbHorizontalLogo,
      defaultClientLogo,
      userInfoDisplayed: false
    };
  },

  computed: {
    ...mapState({
      userProfile: (state) => state.authorisation.userProfile,
      userDisplayName: (state) => state.common.userDisplayName,
      userEmail: (state) => state.common.userEmail,
      excursionCreationButtonDisplayed: (state) => state.common.excursionCreationButtonDisplayed,
      excursionBuilderButtonDisplayed: (state) => state.common.excursionBuilderButtonDisplayed,
      userInitials: (state) => state.common.userInitials,
      isInitClientConfigInProgress: (state) => state.authorisation.isInitClientConfigInProgress
    }),
    ...mapGetters({
      isLoggedIn: 'authorisation/isLoggedIn',
      auth0Enabled: 'authorisation/auth0Enabled',
      silentAxiosError: 'app/silentAxiosError'
    }),
    userInfo() {
      return `<div class="profile-tooltip-content">${JSON.stringify(
        this.userProfile,
        null,
        2
      )}</div>`;
    },
    headerProfile() {
      const userNames = (this.userDisplayName && this.userDisplayName.split(' ')) || [''];
      return new CSHeaderProfileProps(
        {
          givenName: capitalize(userNames[0]),
          surname: capitalize(userNames[1] || userNames[0]),
          email: this.userEmail || ''
        },
        {
          logout: {
            action: this.logout
          }
        }
      );
    },
    sideNavWidth() {
      if (this.isLoggedIn && !this.vendorIntegrationWindowViewEnabled) {
        return 400;
      }

      return 0;
    },

    feedbackBtn() {
      return new CSHeaderButtonProps('btn-feedback', 'Feedback');
    },

    helpBtn() {
      return new CSHeaderButtonProps('btn-help', 'Help', '', this.registerSupportLinkClickEvent);
    },

    settingsBtn() {
      const { clientSiteSettingsButtonDiplayed } = this.$store.state.common;
      if (clientSiteSettingsButtonDiplayed) {
        return new CSHeaderButtonProps('btn-settings', 'Settings', '', this.handleSettingsAction);
      }
      return null;
    },

    version() {
      const { bundleVersion } = packageParam;
      const uiVersion = `${packageParam.version}${packageParam.hotfixIndicator}`;
      const dashApiVersion = this.$store.state.app.apiVersion
        ? `-${this.$store.state.app.apiVersion}`
        : '';

      return `${bundleVersion}#${uiVersion}${dashApiVersion}`;
    },

    topToolbarTextButtonList() {
      return (
        topToolbarTextButtons &&
        topToolbarTextButtons.filter((item) => {
          return `${item.name}` !== 'Clients' && `${item.name}` !== 'SafeTripBuilder';
        })
      );
    },

    excursionCreationTextButton() {
      return (
        topToolbarTextButtons &&
        topToolbarTextButtons.find((item) => {
          return `${item.name}` === 'SafeTripBuilder';
        })
      );
    },

    isResponseWithError: {
      get() {
        return this.$store.state.common.isResponseWithError && !this.silentAxiosError;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, value);
      }
    },
    debugReferenceMessage() {
      let dialogMessage = '';
      dialogMessage = populateErrorMessage({ message: dialogMessage, store: this.$store });
      if (dialogMessage === '') {
        dialogMessage += `<p class="v-card__text debug-error-message mt-3">Unknown Error!</p>`;
      }
      return dialogMessage;
    },
    isDemoEnvironment() {
      return this.$app.env.APP_ENV === 'demo';
    },
    contentHtmlClassConcat() {
      if (!this.vendorIntegrationWindowViewEnabled || this.isOnLoginPage) {
        return '';
      }
      let localContentHtmlClassConcat = 'content';

      switch (this.$route.name) {
        case 'ClientAdminLayout':
          localContentHtmlClassConcat += ' full-width-layout';
          break;

        case 'SupportFeedbackLayout':
          localContentHtmlClassConcat += ' full-width-layout iframe-container-layout';
          break;

        default:
          break;
      }

      return localContentHtmlClassConcat;
    },
    showHeader() {
      return this.isLoggedIn && !this.vendorIntegrationWindowViewEnabled;
    },
    isOnLoginPage() {
      return this.$route.path.toLowerCase() === '/login';
    },
    footerClass() {
      return this.isOnLoginPage && !this.auth0Enabled
        ? 'content stb-login-footer ma-0 mt-auto pb-10'
        : 'content stb-auth0-login-footer ma-0 mt-auto';
    }
  },

  watch: {
    async version() {
      if (!this.$store.state.app.apiVersion) {
        await this.$store.dispatch('app/setApiVersion');
      }
    },
    async isInitClientConfigInProgress(newVal, oldVal) {
      if (oldVal && !newVal) {
        const isVendorAuthScheme =
          !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
        if (!this.isLoggedIn || isVendorAuthScheme) return;
        await this.$store.dispatch('authorisation/getUserProfile', this.$app.stbApiAdapter);
      }
    }
  },
  created() {
    this.$store.dispatch('session/init', this.$app.axiosInstance);
  },
  async mounted() {
    const { eventPluginCommonAdapter, stbApiAdapter } = this.$app;
    const argHash = {
      queryParam: {},
      eventPluginCommonAdapter,
      stbApiAdapter
    };

    await this.$store.dispatch('app/setApiVersion');

    this.$app.eventPluginCommonAdapter.initPluginBatch();
    const { query } = this.$route;

    // TODO: Use different variable to show/hide menu
    this.vendorIntegrationWindowViewEnabled = false;
    if (query.referrer || query['vendor-sid'] || query['oauth-signature'] || this.isOnLoginPage) {
      this.vendorIntegrationWindowViewEnabled = true;
    }
    if (this.isOnLoginPage) {
      await this.$store.dispatch('common/initClientConfig', argHash);
    }
  },

  methods: {
    closeErrorMessageDialogBtnClick() {
      this.isResponseWithError = false;
      this.responseWithErrorMessage = '';
    },
    getLinkItemId(linkItem) {
      return `client-admin-layout--menu-item--link-${linkItem}`;
    },
    logout() {
      this.$store.dispatch('authorisation/logout', this.$route.query);
    },

    redirect(path) {
      if (this.$router.currentRoute.path !== path) {
        this.$router.push(path);
      }
    },

    handleSettingsAction() {
      if (sessionManager.getClientSubdomain() !== '') {
        this.redirect('/admin/clients/users');
      } else {
        this.redirect('/admin/clients');
      }
    },

    registerFeedbackLinkClickEvent(jsEvent, href) {
      jsEvent?.preventDefault();
      const that = this;
      const trackableEventBaseArgHash = {
        defaultParams: true
      };
      const trackableEventOptionArgHash = {
        sentImmediately: true
      };
      const registerFeedbackLinkClickEventCb = () => {
        // This callback is used to prevent tracking interruption by page redirection
        that.$app.window.location = href;
      };

      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.FEEDBACK_LINK_CLICK,
        trackableEventBaseArgHash,
        trackableEventOptionArgHash,
        registerFeedbackLinkClickEventCb
      );

      this.redirect(href);
    },

    registerSupportLinkClickEvent() {
      const trackableEventBaseArgHash = {
        defaultParams: true
      };

      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.SUPPORT_LINK_CLICK,
        trackableEventBaseArgHash
      );

      window.open(
        'https://complispace.zendesk.com/hc/en-us/categories/900001334623-Safe-Excursions-PlanCheckGo-SafeTripBuilder-'
      );
    },

    resetStoreState() {
      this.$store.commit(types.COMMON_SET_EXCURSION_TYPE, '');
      const excludedAttributeList = [
        'allProviders',
        'allRisks',
        'apiDataDefaultJurisdiction',
        'excursionTypeChoiceList',
        'apiParam',
        'excursionDc',
        'excursionAdditionalInformationEnabled',
        'logoAttachmentUrl',
        'vendorExcursionSidInputEnabled',
        'clientUsersTabDisplayed',
        'usersTabDisplayed',
        'clientsTabDisplayed',
        'clientSiteSettingsButtonDiplayed',
        'clientSelfGroupManagementDisplayed',
        'vendorGroupIdDisplayEnabled',
        'publicSchoolFeatureSetEnabled',
        'riskCategoryManagementMenuItemDisplayed',
        'operationManagementMenuItemDisplayed',
        'studentManagementSubjectManagementDisplayed'
      ];
      const argHash = {
        excludedAttributeList,
        storeModuleName: ''
      };
      this.$store.commit(types.COMMON_RESET_STORE_MODULE_STATE, argHash);
      window.location.reload();
    },
    textButtonDisplayed(textButton) {
      let localTextButtonDisplayed = false;

      if (
        textButton &&
        ((this.excursionBuilderButtonDisplayed && textButton.name === 'ExcursionBuilder') ||
          (this.excursionCreationButtonDisplayed && textButton.name === 'SafeTripBuilder') ||
          (textButton.name !== 'ExcursionBuilder' && textButton.name !== 'SafeTripBuilder'))
      ) {
        localTextButtonDisplayed = true;
      }

      return localTextButtonDisplayed;
    },
    redirectToUrl(url, newWindowEnabled) {
      if (newWindowEnabled) {
        window.open(url);
      } else {
        window.location.href = url;
      }
    },
    getCopyrightYearRange() {
      const startYear = '2018';
      return `${startYear}-${new Date().getFullYear().toString()}`;
    }
  }
};
</script>
<style>
.admin-management-container {
  margin: 50px;
}
.debug-error-message {
  color: red;
  margin: 0px 0px !important;
  padding: 0px 0px !important;
}
.topbar-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.profile-tooltip-content {
  max-width: 420px;
  padding-top: 2px;
  padding-left: 2px;
  padding-right: 2px;
  word-wrap: break-word;
  clear: right;
  white-space: pre;
  line-height: 1;
}
.stb-login-footer {
  min-width: 100%;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stb-app .cs-footer {
  padding: 0px;
}
.stb-auth0-login-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100%;
}
</style>
<style scoped>
.demo-note {
  padding: 10px;
}
:deep(#cs-side-nav) {
  background-color: rgb(var(--v-theme-black-lighten-3));
  color: #ffffff;
}
</style>
